import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  ArrowRight,
  Loader2,
  Building2,
  Sparkles,
  Shield,
  Users,
  BarChart3,
  AlertCircle,
} from "lucide-react";

import { loginUser } from "../api/auth";
import ForgotPasswordDialog, { useForgotPassword } from "./Forgotpassworddialog";

export default function Login() {
  // ProtectedRoute redirects an unauthenticated visit to "/" with
  // state: { from: location } (the page they were actually trying to
  // reach, e.g. /leads/15/raise-opf from an email link). Read that back
  // out here so a successful login can send them on to it instead of
  // always dropping them on /dashboard.
  const location = useLocation();
  const from = location.state?.from;
  const requestedRedirect = from
    ? `${from.pathname || ""}${from.search || ""}${from.hash || ""}`
    : null;

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(""); // Captures Flask invalid credentials/server errors
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openForgotPassword, forgotPasswordState, closeForgotPassword } = useForgotPassword();

  const stats = [
    {
      title: "Organizations",
      value: "2,500+",
      icon: <Building2 size={20} />,
    },
    {
      title: "Employees",
      value: "1.2 Lakh",
      icon: <Users size={20} />,
    },
    {
      title: "Revenue Managed",
      value: "₹850 Cr+",
      icon: <BarChart3 size={20} />,
    },
  ];

  const validate = () => {
    let temp = {};
    setApiError(""); // Clear any previous API errors when validating a new attempt

    if (!form.email.trim()) {
      temp.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    ) {
      temp.email = "Invalid email address";
    }

    if (!form.password.trim()) {
      temp.password = "Password is required";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      setApiError(""); // Clear any legacy error state

      // Sending the exact payload parameters expected by your Flask backend
      const { data } = await loginUser({
        usr_email: form.email,
        usr_password: form.password,
      });

      // ===================================================
      // CRITICAL INTEGRATION FIX
      // Storing access token, sliding refresh token, and user data
      // ===================================================
      localStorage.setItem("token", data.token);
      localStorage.setItem("refresh_token", data.refresh_token); 
      localStorage.setItem("user", JSON.stringify(data.user));

      // Presales and Bid Manager users work primarily from Opportunities,
      // so use /leads as their default landing page. A protected deep link
      // still takes priority when login was triggered from a specific page.
      const department = (
        data.user?.department ||
        data.user?.usr_department ||
        ""
      )
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

      const departmentHome =
        department === "presales" || department === "bidmanager"
          ? "/leads"
          : "/dashboard";

      // Redirecting immediately while keeping the loader active to avoid a flash.
      // A full navigation (not client-side) so the rest of the app boots up
      // fresh with the just-stored token/user already in localStorage.
      window.location.href = requestedRedirect || departmentHome;

    } catch (error) {
      // Turn off loading so user can try again
      setLoading(false);

      if (error.response && error.response.data) {
        // Set the dynamic backend message (e.g., "Invalid username or password")
        setApiError(error.response.data.message || "Invalid username or password.");
      } else {
        setApiError("Unable to connect to server. Please check your connection.");
      }
    }
  };

  return (
    <>
    <div className="min-h-screen bg-slate-100 flex">

      {/* ================= LEFT BRANDING ================= */}
      <div className="hidden xl:flex relative w-[48%] overflow-hidden bg-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-blue-700 blur-[130px] opacity-40"></div>
          <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-cyan-500 blur-[150px] opacity-30"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.05)_1px,transparent_1px)] bg-[size:35px_35px]"></div>
        </div>

        {/* Content */}
        <div className="relative z-20 flex flex-col justify-between h-full p-8 2xl:p-14 text-white">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center shadow-xl">
                <Sparkles size={28} />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-wide">Enterprise SPF</h1>
                <p className="text-blue-200 text-sm mt-1">Business Management Platform</p>
              </div>
            </div>

            <div className="mt-20 max-w-xl">
              <span className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/40 px-4 py-2 rounded-full text-sm">
                <ShieldCheck size={18} /> Secure Enterprise Workspace
              </span>
              <h2 className="mt-8 text-4xl 2xl:text-6xl leading-tight font-black">
                Manage Your Entire Business <span className="block text-blue-400">From One Platform.</span>
              </h2>
              <p className="mt-8 text-lg text-slate-300 leading-8">
                Streamline Sales, CRM, HRMS, Inventory, Finance, Quotations, Projects, Analytics,
                Employee Management and Customer Success using one unified enterprise platform.
              </p>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-5">
            {stats.map((item, index) => (
              <div key={index} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 p-5">
                <div className="text-blue-300 mb-4">{item.icon}</div>
                <h3 className="text-3xl font-black tabular-nums">{item.value}</h3>
                <p className="text-slate-300 text-sm mt-2">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= RIGHT LOGIN FORM ================= */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-10 bg-slate-100">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-10">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
                <Shield size={30} className="text-white" />
              </div>
            </div>

            <h2 className="mt-6 text-center text-4xl font-black text-slate-900">Welcome</h2>
            <p className="text-center text-slate-500 mt-3">Sign in to continue to your workspace</p>

            <div className="mt-10 space-y-6">
              
              {/* Highlighted In-Page API Error Alert Box */}
              {apiError && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl animate-in fade-in duration-200">
                  <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                  <div className="text-sm font-medium">{apiError}</div>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-6">
                {/* Email Input */}
                <div>
                  <label className="block mb-2 text-sm font-semibold text-slate-700">Email Address</label>
                  <div className={`relative rounded-xl border transition-all ${errors.email ? "border-red-400 ring-2 ring-red-100" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-100"}`}>
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      placeholder="name@company.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl outline-none bg-transparent text-slate-800 placeholder:text-slate-400"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
                </div>

                {/* Password Input */}
                <div>
                  <label className="block mb-2 text-sm font-semibold text-slate-700">Password</label>
                  <div className={`relative rounded-xl border transition-all ${errors.password ? "border-red-400 ring-2 ring-red-100" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-100"}`}>
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full pl-12 pr-12 py-3.5 rounded-xl outline-none bg-transparent text-slate-800 placeholder:text-slate-400"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition">
                      {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-2">{errors.password}</p>}
                </div>

                {/* Options */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.remember}
                      onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-600">Remember Me</span>
                  </label>
                  <button type="button" onClick={openForgotPassword} className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition">
                    Forgot Password?
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all duration-200 text-white rounded-xl py-3.5 font-semibold shadow-lg shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" /> Signing In...
                    </>
                  ) : (
                    <>
                      Sign In <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              {/* Security Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-slate-400">Enterprise Security</span>
                </div>
              </div>

              {/* Security Badges */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <ShieldCheck size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Secure Login</p>
                      <p className="text-xs text-slate-500">SSL Encrypted</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Lock size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Protected</p>
                      <p className="text-xs text-slate-500">Role Based</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Administrator Contact */}
              <div className="pt-3 text-center">
                <p className="text-sm text-slate-500">Need access to the CRM?</p>
                <button type="button" className="mt-2 text-blue-600 hover:text-blue-700 font-semibold transition">
                  Contact Administrator
                </button>
              </div>

            </div>
          </div>

          <div className="mt-10 text-center text-xs text-slate-400">
            © 2026 Enterprise CRM Platform.<br />All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
    <ForgotPasswordDialog forgotPasswordState={forgotPasswordState} onClose={closeForgotPassword} />
    </>
  );
}
